import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { LoadingBox } from "@/components/ui";
import { MarkdownBlock } from "../components/markdown-block.tsx";
import { PageHeader } from "../components/page-header";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useMemberInfo } from "../hooks/use-member-info.tsx";
import { useSafeNavigateBack } from "../hooks/use-safe-navigate-back.ts";
import type { Issue, IssueOccurrence } from "../types.ts";
import {
  getIssueSeverityColor as getSeverityColor,
  getIssueStatusColor as getStatusColor,
  getIssueTypeColor as getTypeColor,
} from "../utils/issue-colors";
import { humanizeText } from "../utils/text.ts";

export default function IssueDetailPage() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigateBack = useSafeNavigateBack("/issues");
  const { enqueueSnackbar } = useSnackbar();

  const { getMember } = useMemberInfo();

  const {
    data: issue,
    isLoading: isLoadingIssue,
    refetch: refetchIssue,
  } = useApiQuery<Issue>(`/issues/${issueId}`);

  const { data: occurrences, isLoading: isLoadingOccurrences } = useApiQuery<IssueOccurrence[]>(
    `/issues/${issueId}/occurrences`,
  );

  const { mutate: ignoreIssue, isPending: isIgnoring } = useApiMutation(
    "PATCH",
    `/issues/${issueId}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Issue ignored successfully", {
          variant: "success",
        });
        refetchIssue();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to ignore issue: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );

  const { mutate: resolveIssue, isPending: isResolving } = useApiMutation(
    "PATCH",
    `/issues/${issueId}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Issue resolved successfully", {
          variant: "success",
        });
        refetchIssue();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to resolve issue: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );

  const handleIgnore = () => {
    ignoreIssue({
      issue_status: "closed",
    });
  };

  const handleResolve = () => {
    resolveIssue({
      issue_status: "resolved",
    });
  };

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [stableRecordingUrl, setStableRecordingUrl] = useState<string | null>(null);

  const seekTo = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;

    // Pause the video if it's playing
    if (!v.paused) {
      v.pause();
    }

    if (v.readyState < 1) {
      // Wait until metadata is loaded so duration/currentTime are valid
      v.addEventListener(
        "loadedmetadata",
        () => {
          v.currentTime = seconds;
        },
        { once: true },
      );
    } else {
      v.currentTime = seconds;
    }
  }, []);

  useEffect(() => {
    // Set the stable recording URL once, ignore signature refreshes
    if (issue?.recording_path && !stableRecordingUrl) {
      setStableRecordingUrl(issue.recording_path);
    }
  }, [issue?.recording_path, stableRecordingUrl]);

  useEffect(() => {
    // Reset stable URL when issue changes
    if (issueId) {
      setStableRecordingUrl(null);
    }
  }, [issueId]);

  useEffect(() => {
    if (stableRecordingUrl && issue?.recording_timestamps) {
      // If there's a recording and timestamps, seek to the first timestamp
      const firstTimestamp = issue.recording_timestamps[0];
      if (firstTimestamp) {
        seekTo(firstTimestamp);
      }
    }
  }, [stableRecordingUrl, issue?.recording_timestamps, seekTo]);

  if (isLoadingIssue) {
    return <LoadingBox sx={{ height: "50vh" }} />;
  }

  if (!issue) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Issue not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Tooltip title="Back to Issues">
              <IconButton onClick={navigateBack} color="primary" size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" component="h1" gutterBottom>
              {issue.title}
            </Typography>
          </Stack>
        }
        subtitle={
          <Typography variant="subtitle1" color="text.secondary" align="left">
            <code>{issue.slug}</code>
          </Typography>
        }
      />

      <Grid container spacing={{ xs: 2 }} sx={{ pt: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                {/* Status and Action Buttons */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={humanizeText(issue.issue_status)}
                      color={getStatusColor(issue.issue_status)}
                      size="medium"
                    />
                    <Chip
                      label={humanizeText(issue.severity)}
                      color={getSeverityColor(issue.severity)}
                      size="medium"
                    />
                    <Chip
                      label={humanizeText(issue.issue_type)}
                      color={getTypeColor(issue.issue_type)}
                      size="medium"
                      variant="outlined"
                    />
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<BlockIcon />}
                      onClick={handleIgnore}
                      disabled={isIgnoring || issue.issue_status === "closed"}
                      size="small"
                    >
                      {isIgnoring ? "Ignoring..." : "Ignore"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleResolve}
                      disabled={isResolving || issue.issue_status === "resolved"}
                      size="small"
                    >
                      {isResolving ? "Resolving..." : "Resolve"}
                    </Button>
                  </Stack>
                </Stack>

                <Divider />

                {/* Description */}
                <Stack spacing={0}>
                  <Typography variant="h5" gutterBottom>
                    Description
                  </Typography>
                  <MarkdownBlock content={issue.description || "No description provided."} />
                </Stack>

                {/* Help Text */}
                {issue.help && (
                  <Stack spacing={0}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="flex-start"
                    >
                      <Typography variant="h6" gutterBottom>
                        Help
                      </Typography>
                      {issue.help_url && (
                        <Link href={issue.help_url} target="_blank" rel="noopener noreferrer">
                          <Typography variant="body2" color="primary">
                            Learn more
                          </Typography>
                        </Link>
                      )}
                    </Stack>
                    <MarkdownBlock content={issue.help} />
                  </Stack>
                )}

                {/* Recommendations */}
                {issue.recommendations && issue.recommendations.length > 0 && (
                  <Stack spacing={0}>
                    <Typography variant="h6" gutterBottom>
                      Recommendations
                    </Typography>
                    <Stack>
                      {issue.recommendations.map((recommendation, index) => (
                        <Typography key={index} variant="body1" gutterBottom>
                          {index + 1}. {recommendation}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                )}

                <Stack direction="row" spacing={2}>
                  {issue.pre_screenshot_path && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Before
                      </Typography>
                      <Box
                        component="img"
                        src={issue.pre_screenshot_path}
                        alt="Before Screenshot"
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          maxHeight: "400px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  {issue.post_screenshot_path && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        After
                      </Typography>
                      <Box
                        component="img"
                        src={issue.post_screenshot_path}
                        alt="After Screenshot"
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          maxHeight: "400px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                </Stack>

                {stableRecordingUrl && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Recording
                    </Typography>
                    <Box
                      component="video"
                      src={stableRecordingUrl}
                      ref={videoRef}
                      controls
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        maxHeight: "500px",
                        backgroundColor: "black",
                      }}
                    >
                      Your browser does not support the video tag.
                    </Box>
                  </Box>
                )}

                {/* Tags */}
                {issue.tags && issue.tags.length > 0 && (
                  <Stack spacing={0}>
                    <Typography variant="h6" gutterBottom>
                      Tags
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {issue.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {/* Issue Metadata */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Issue Details
              </Typography>
              <Stack spacing={2}>
                <Stack spacing={0}>
                  <Typography variant="caption" color="text.secondary">
                    Last Seen
                  </Typography>
                  <Typography variant="body2">
                    {formatDistanceToNow(new Date(issue.last_seen), {
                      addSuffix: true,
                    })}
                  </Typography>
                </Stack>

                <Stack spacing={0}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {formatDistanceToNow(new Date(issue.created_at), {
                      addSuffix: true,
                    })}
                  </Typography>
                </Stack>

                <Stack spacing={0}>
                  <Typography variant="caption" color="text.secondary">
                    Resolved
                  </Typography>
                  <Typography variant="body2">
                    {issue.resolved_at
                      ? formatDistanceToNow(new Date(issue.resolved_at), {
                          addSuffix: true,
                        })
                      : "-"}{" "}
                    {issue.resolved_by ? `by ${getMember(issue.resolved_by).name}` : ""}
                  </Typography>
                </Stack>

                <Stack spacing={0}>
                  <Typography variant="caption" color="text.secondary">
                    Closed
                  </Typography>
                  <Typography variant="body2">
                    {issue.closed_at
                      ? formatDistanceToNow(new Date(issue.closed_at), {
                          addSuffix: true,
                        })
                      : "-"}{" "}
                    {issue.closed_by ? `by ${getMember(issue.closed_by).name}` : ""}
                  </Typography>
                </Stack>

                {issue.owner && (
                  <Stack spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Owner
                    </Typography>
                    <Typography variant="body2">{getMember(issue.owner).name}</Typography>
                  </Stack>
                )}

                {issue.url && (
                  <Stack spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      URL
                    </Typography>
                    <Link
                      href={issue.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      {issue.url}
                    </Link>
                  </Stack>
                )}

                <Stack spacing={0}>
                  <Typography variant="caption" color="text.secondary">
                    Reporter Engine
                  </Typography>
                  <Typography variant="body2">
                    <code>{issue.reporter}</code>
                  </Typography>
                </Stack>

                {issue.test_id && (
                  <Stack spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Test ID
                    </Typography>
                    <Link
                      href={`/tests-main/tests/${issue.test_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      <code>{issue.test_id.slice(0, 8)}</code>
                    </Link>
                  </Stack>
                )}

                {issue.scenario_id && (
                  <Stack spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Scenario ID
                    </Typography>
                    <Link
                      href={`/tests-main/scenarios/${issue.scenario_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      <code>{issue.scenario_id.slice(0, 8)}</code>
                    </Link>
                  </Stack>
                )}

                {issue.discovery_run_id && (
                  <Stack spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Discovery Run
                    </Typography>
                    <Link
                      href={`/discovery-runs/${issue.discovery_run_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      <code>{issue.discovery_run_id.slice(0, 8)}</code>
                    </Link>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          {/* Recent Occurrences */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Occurrences
              </Typography>
              {isLoadingOccurrences ? (
                <CircularProgress size={24} />
              ) : occurrences && occurrences.length > 0 ? (
                <Stack spacing={2} divider={<Divider />}>
                  {occurrences.slice(0, 5).map((occurrence) => (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      key={occurrence.id}
                    >
                      <Stack direction="column" spacing={0} alignItems="flex-start">
                        <Typography variant="body2" color="text.primary">
                          {occurrence.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(occurrence.created_at), {
                            addSuffix: true,
                          })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {occurrence.test_run_id && (
                          <Link
                            href={`/runs/${occurrence.test_run_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Chip label={`Test Run`} size="small" variant="outlined" />
                          </Link>
                        )}
                        {occurrence.test_suite_run_id && (
                          <Link
                            href={`/suite-runs/${occurrence.test_suite_run_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Chip label={`Test Suite Run`} size="small" variant="outlined" />
                          </Link>
                        )}
                        {occurrence.discovery_run_id && (
                          <Link
                            href={`/discovery-runs/${occurrence.discovery_run_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Chip label={`Discovery Run`} size="small" variant="outlined" />
                          </Link>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                  {occurrences.length > 5 && (
                    <Typography variant="body2" color="text.secondary">
                      ...and {occurrences.length - 5} more
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No occurrences found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
